/**
 * Buyer-Demand admin — manual project create/edit form.
 *
 * Phase 1 shipped the Basics tab. Phase 2 adds the Qualifications tab
 * (canonical attribute picker + custom question builder + live
 * feasibility count). Quotas (Phase 3) is rendered as a disabled tab
 * placeholder so the navigation shape is final.
 *
 * The "Static redirect URLs" panel surfaces the four buyer-emailable
 * URLs so the admin can copy them and send to the buyer's integration
 * contact during onboarding.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Save, Trash2, Plus } from 'lucide-react';
import { API_BASE } from '../../lib/apiBase';
import {
  createManualProject,
  fetchBuyers,
  fetchFeasibility,
  fetchManualProject,
  updateManualProject,
  type AttrQualification,
  type CustomQuestion,
  type ManualQualEntry,
  type QuotaCell,
} from '../../lib/adminApi';
import QualificationPicker from '../../components/QualificationPicker';
import CustomQuestionBuilder from '../../components/CustomQuestionBuilder';
import QuotaBuilder from '../../components/QuotaBuilder';
import QuotaProgress from '../../components/QuotaProgress';
import StatsPanel from '../../components/StatsPanel';

const REDIRECT_OUTCOMES = ['complete', 'screenout', 'quotafull', 'quality'] as const;

function staticRedirectUrl(outcome: typeof REDIRECT_OUTCOMES[number]): string {
  // {SESSION_ID} is the placeholder the buyer's platform substitutes at
  // runtime. Phase 5 (2026-05-09) — session_id is the sole authenticity
  // token; no signature parameter.
  return `${API_BASE}/x/manual/cb/${outcome}?sid={SESSION_ID}`;
}

export default function AdminProjectForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const { data: buyersData } = useQuery({
    queryKey: ['admin', 'buyers'],
    queryFn: fetchBuyers,
  });

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['admin', 'manual-project', id],
    queryFn: () => fetchManualProject(id!),
    enabled: isEdit,
  });

  const [tab, setTab] = useState<'basics' | 'qualifications' | 'quotas'>('basics');
  const [buyerId, setBuyerId] = useState(searchParams.get('buyer_id') ?? '');
  const [reference, setReference] = useState('');
  const [country, setCountry] = useState('US');
  const [language, setLanguage] = useState('en');
  const [completes, setCompletes] = useState(100);
  const [cpi, setCpi] = useState(1.5);
  const [loi, setLoi] = useState(10);
  const [ir, setIr] = useState(50);
  const [entryUrl, setEntryUrl] = useState('');
  const [qualifications, setQualifications] = useState<ManualQualEntry[]>([]);
  const [quotas, setQuotas] = useState<QuotaCell[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing?.project) {
      const p = existing.project;
      setBuyerId(p.buyer_id ?? '');
      setReference(p.survey_name);
      setCompletes(p.completes_remaining);
      setCpi(p.cpi);
      setLoi(p.loi);
      setIr(p.ir);
      setEntryUrl(p.entry_url ?? '');
      // qualifications + quotas come back as JSON; cast through unknown
      // so the narrow union/object types stay the contract.
      const rawQuals = (p as unknown as { qualifications?: unknown }).qualifications;
      if (Array.isArray(rawQuals)) {
        setQualifications(rawQuals as ManualQualEntry[]);
      }
      const rawQuotas = (p as unknown as { quotas?: unknown }).quotas;
      if (Array.isArray(rawQuotas)) {
        setQuotas(rawQuotas as QuotaCell[]);
      }
    }
  }, [existing]);

  const createMutation = useMutation({
    mutationFn: createManualProject,
    onSuccess: ({ project }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'buyer', project.buyer_id, 'projects'] });
      navigate(`/admin/manual-projects/${project.id}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateManualProject>[1] & { setLive?: boolean }) =>
      updateManualProject(id!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'manual-project', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'buyer', existing?.project.buyer_id, 'projects'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  function handleSave(setLive = false) {
    setError(null);
    if (!buyerId) { setError('Select a buyer.'); return; }
    if (!reference) { setError('Reference is required.'); return; }
    if (!entryUrl) { setError('Entry URL is required.'); return; }

    const base = { reference, completes, cpi, loi, ir, entry_url: entryUrl };

    if (isEdit) {
      updateMutation.mutate({
        ...base,
        qualifications,
        quotas,
        status: setLive ? 'LIVE' : undefined,
      });
    } else {
      createMutation.mutate({
        buyer_id: buyerId,
        ...base,
        country,
        language,
        status: setLive ? 'LIVE' : 'PAUSED',
      });
    }
  }

  const projectId = useMemo(() => existing?.project.id ?? '(unsaved)', [existing]);

  if (isEdit && loadingExisting) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <Link
        to={existing?.project.buyer_id ? `/admin/buyers/${existing.project.buyer_id}` : '/admin/buyers'}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white"
      >
        <ArrowLeft className="w-3 h-3" /> Back
      </Link>

      <h1 className="text-2xl font-bold font-display text-white">
        {isEdit ? `Edit project: ${existing?.project.survey_name ?? ''}` : 'New manual project'}
      </h1>

      {isEdit && existing?.project && (
        <StatusStrip
          status={(existing.project.status ?? 'PAUSED') as 'LIVE' | 'PAUSED' | 'CLOSED'}
          onTransition={(next) => {
            setError(null);
            updateMutation.mutate({ status: next });
          }}
          pending={updateMutation.isPending}
        />
      )}

      <div className="flex gap-1 border-b border-slate-700">
        <TabButton active={tab === 'basics'} onClick={() => setTab('basics')}>Basics</TabButton>
        <TabButton active={tab === 'qualifications'} onClick={() => setTab('qualifications')}>
          Qualifications {qualifications.length > 0 && `(${qualifications.length})`}
        </TabButton>
        <TabButton
          active={tab === 'quotas'}
          onClick={isEdit ? () => setTab('quotas') : undefined}
          disabled={!isEdit}
        >
          Quotas {quotas.length > 0 && `(${quotas.length})`}
          {!isEdit && <span className="text-[10px] opacity-60 ml-1">(save first)</span>}
        </TabButton>
      </div>

      {tab === 'qualifications' ? (
        <QualificationsTab
          qualifications={qualifications}
          onAdd={(q) => setQualifications((prev) => [...prev, q])}
          onRemove={(idx) => setQualifications((prev) => prev.filter((_, i) => i !== idx))}
          pickerOpen={pickerOpen}
          builderOpen={builderOpen}
          onOpenPicker={() => setPickerOpen(true)}
          onOpenBuilder={() => setBuilderOpen(true)}
          onClosePicker={() => setPickerOpen(false)}
          onCloseBuilder={() => setBuilderOpen(false)}
        />
      ) : tab === 'quotas' ? (
        <QuotasTab
          surveyId={id ?? ''}
          quotas={quotas}
          onChange={setQuotas}
          isClosed={(existing?.project.status ?? 'PAUSED') === 'CLOSED'}
          isLive={(existing?.project.status ?? 'PAUSED') === 'LIVE'}
        />
      ) : (
      <div className="bg-slate-800 rounded-xl p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400">Buyer</label>
          <select
            value={buyerId}
            onChange={(e) => setBuyerId(e.target.value)}
            disabled={isEdit}
            className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 disabled:opacity-50"
          >
            <option value="">— select buyer —</option>
            {(buyersData?.buyers ?? []).map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400">Reference</label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. TestSet Q2 Tracker"
            className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">Country</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={isEdit}
              className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 disabled:opacity-50"
            >
              <option value="US">United States</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={isEdit}
              className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 disabled:opacity-50"
            >
              <option value="en">English</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">Completes target</label>
            <input
              type="number"
              min={1}
              value={completes}
              onChange={(e) => setCompletes(parseInt(e.target.value, 10) || 0)}
              className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">CPI ($)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={cpi}
              onChange={(e) => setCpi(parseFloat(e.target.value) || 0)}
              className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">LOI (minutes)</label>
            <input
              type="number"
              min={1}
              value={loi}
              onChange={(e) => setLoi(parseInt(e.target.value, 10) || 0)}
              className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-400">IR (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={ir}
              onChange={(e) => setIr(parseInt(e.target.value, 10) || 0)}
              className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700"
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wide text-slate-400">Buyer entry URL</label>
          <input
            type="url"
            value={entryUrl}
            onChange={(e) => setEntryUrl(e.target.value)}
            placeholder="https://buyer.example.com/survey/abc"
            className="mt-1 w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 font-mono"
          />
          <p className="mt-1 text-xs text-slate-500">
            We append <code>?sid={'{SESSION_ID}'}</code> when handing the member off.
          </p>
        </div>

      </div>
      )}

      {error && <p className="text-sm text-red-400">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={() => handleSave(false)}
          disabled={createMutation.isPending || updateMutation.isPending}
          className="inline-flex items-center gap-2 bg-slate-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-600 disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> Save draft
        </button>
        <button
          onClick={() => handleSave(true)}
          disabled={createMutation.isPending || updateMutation.isPending}
          className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          Save &amp; set live
        </button>
      </div>

      <div className="bg-slate-800 rounded-xl p-6 space-y-3">
        <h2 className="text-sm font-bold text-white uppercase tracking-wide">Static redirect URLs</h2>
        <p className="text-xs text-slate-400">
          Copy these into the buyer's platform. The <code className="text-slate-300">{'{SESSION_ID}'}</code> placeholder
          is substituted by the buyer's platform when they redirect respondents back. The same four URLs work
          for every project under this buyer — our session_id resolves to the specific project.
        </p>
        <div className="space-y-2">
          {REDIRECT_OUTCOMES.map((outcome) => (
            <RedirectUrlRow key={outcome} outcome={outcome} url={staticRedirectUrl(outcome)} />
          ))}
        </div>
        {isEdit && (
          <p className="text-xs text-slate-500 mt-2">Project ID: <span className="font-mono">{projectId}</span></p>
        )}
      </div>
    </div>
  );
}

type ProjectStatus = 'LIVE' | 'PAUSED' | 'CLOSED';

function StatusStrip({
  status,
  onTransition,
  pending,
}: {
  status: ProjectStatus;
  onTransition: (next: ProjectStatus) => void;
  pending: boolean;
}) {
  const [confirmingClose, setConfirmingClose] = useState(false);

  const badge =
    status === 'LIVE' ? { text: 'Live', cls: 'bg-green-500/20 text-green-300 border-green-500/30' } :
    status === 'PAUSED' ? { text: 'Paused', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/30' } :
    { text: 'Closed', cls: 'bg-slate-600/40 text-slate-300 border-slate-600/40' };

  const buttons: Array<{ label: string; next: ProjectStatus; style: 'primary' | 'secondary' | 'danger' }> = [];
  if (status === 'PAUSED') {
    buttons.push({ label: 'Set Live', next: 'LIVE', style: 'primary' });
    buttons.push({ label: 'Close project', next: 'CLOSED', style: 'danger' });
  } else if (status === 'LIVE') {
    buttons.push({ label: 'Pause', next: 'PAUSED', style: 'secondary' });
    buttons.push({ label: 'Close project', next: 'CLOSED', style: 'danger' });
  }
  // CLOSED: terminal — no buttons.

  function handleClick(next: ProjectStatus) {
    if (next === 'CLOSED') {
      setConfirmingClose(true);
      return;
    }
    onTransition(next);
  }

  return (
    <div className="flex items-center gap-3 bg-slate-800 rounded-xl p-4">
      <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${badge.cls}`}>{badge.text}</span>
      <div className="flex-1" />
      {status === 'CLOSED' && (
        <span className="text-xs text-slate-500">Terminal — this project can't be re-opened.</span>
      )}
      <div className="flex gap-2">
        {buttons.map((b) => (
          <button
            key={b.label}
            onClick={() => handleClick(b.next)}
            disabled={pending}
            className={
              b.style === 'primary'
                ? 'bg-primary text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-primary/90 disabled:opacity-50'
                : b.style === 'danger'
                ? 'border border-red-500/40 text-red-300 text-sm px-3 py-1.5 rounded-lg hover:bg-red-500/10 disabled:opacity-50'
                : 'bg-slate-700 text-white text-sm px-3 py-1.5 rounded-lg hover:bg-slate-600 disabled:opacity-50'
            }
          >
            {b.label}
          </button>
        ))}
      </div>

      {confirmingClose && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-xl p-6 w-full max-w-md space-y-4 border border-red-500/30">
            <h2 className="text-lg font-bold text-white">Close this project?</h2>
            <p className="text-sm text-slate-400">
              Members will no longer see this project on their wall, and active sessions
              already in flight will finish normally. Closing is permanent — the project
              cannot be re-opened.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirmingClose(false)}
                disabled={pending}
                className="text-sm px-4 py-2 text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => { setConfirmingClose(false); onTransition('CLOSED'); }}
                disabled={pending}
                className="bg-red-500 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                Yes, close project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'text-white border-primary'
          : disabled
            ? 'text-slate-600 border-transparent cursor-not-allowed'
            : 'text-slate-400 hover:text-white border-transparent'
      }`}
    >
      {children}
    </button>
  );
}

interface QualificationsTabProps {
  qualifications: ManualQualEntry[];
  onAdd: (q: ManualQualEntry) => void;
  onRemove: (idx: number) => void;
  pickerOpen: boolean;
  builderOpen: boolean;
  onOpenPicker: () => void;
  onOpenBuilder: () => void;
  onClosePicker: () => void;
  onCloseBuilder: () => void;
}

function QualificationsTab(props: QualificationsTabProps) {
  const {
    qualifications, onAdd, onRemove,
    pickerOpen, builderOpen, onOpenPicker, onOpenBuilder, onClosePicker, onCloseBuilder,
  } = props;

  const feasibility = useFeasibility(qualifications);

  return (
    <>
      <div className="bg-slate-800 rounded-xl p-6 space-y-4">
        <div>
          <p className="text-sm font-bold text-white">Qualifications</p>
          <p className="text-xs text-slate-400 mt-1">
            Members must match every qualification to be eligible. Custom
            questions gate the entry — members must answer to qualify.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onOpenPicker}
            className="inline-flex items-center gap-1 bg-primary text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" /> Add qualification
          </button>
          <button
            onClick={onOpenBuilder}
            className="inline-flex items-center gap-1 bg-slate-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-slate-600"
          >
            <Plus className="w-4 h-4" /> Add custom question
          </button>
        </div>

        {qualifications.length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            No qualifications yet — every member qualifies.
          </p>
        ) : (
          <ul className="space-y-2">
            {qualifications.map((q, idx) => (
              <li
                key={idx}
                className="flex items-start justify-between bg-slate-900 rounded-lg p-3 border border-slate-700"
              >
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{describeQual(q)}</p>
                  <p className="text-xs text-slate-500 mt-1">{describeRule(q)}</p>
                </div>
                <button
                  onClick={() => onRemove(idx)}
                  className="text-slate-500 hover:text-red-400"
                  aria-label="Remove qualification"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-slate-800 rounded-xl p-4">
        <p className="text-xs uppercase tracking-wide text-slate-400">Live feasibility</p>
        {feasibility.isLoading ? (
          <p className="text-sm text-slate-400 mt-1">Computing…</p>
        ) : feasibility.data ? (
          <p className="text-sm text-white mt-1">
            <span className="font-bold text-lg">
              {feasibility.data.panelMatch.toLocaleString()}
            </span>{' '}
            of {feasibility.data.totalActiveMembers.toLocaleString()} active members match these
            qualifications today.
          </p>
        ) : (
          <p className="text-sm text-slate-500 mt-1">—</p>
        )}
      </div>

      {pickerOpen && (
        <QualificationPicker
          onSave={(q) => { onAdd(q); onClosePicker(); }}
          onClose={onClosePicker}
        />
      )}
      {builderOpen && (
        <CustomQuestionBuilder
          onSave={(q) => { onAdd(q); onCloseBuilder(); }}
          onClose={onCloseBuilder}
        />
      )}
    </>
  );
}

interface QuotasTabProps {
  surveyId: string;
  quotas: QuotaCell[];
  onChange: (cells: QuotaCell[]) => void;
  isClosed: boolean;
  isLive: boolean;
}

function QuotasTab({ surveyId, quotas, onChange, isClosed, isLive }: QuotasTabProps) {
  return (
    <>
      <div className="bg-slate-800 rounded-xl p-6">
        <QuotaBuilder
          value={quotas}
          onChange={onChange}
          disabled={isClosed}
          surveyId={surveyId || undefined}
        />
        {isClosed && (
          <p className="text-xs text-slate-500 italic mt-3">
            Project is closed — quota definitions are read-only.
          </p>
        )}
      </div>
      {surveyId && <QuotaProgress surveyId={surveyId} isActive={isLive} />}
      {surveyId && <StatsPanel surveyId={surveyId} isLive={isLive} />}
    </>
  );
}

/**
 * Live feasibility query, debounced ~500ms after the qualifications
 * array changes so the admin can edit without hammering the API.
 */
function useFeasibility(qualifications: ManualQualEntry[]) {
  const [debounced, setDebounced] = useState(qualifications);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(qualifications), 500);
    return () => clearTimeout(t);
  }, [qualifications]);

  return useQuery({
    queryKey: ['admin', 'feasibility', JSON.stringify(debounced)],
    queryFn: () => fetchFeasibility(debounced),
    staleTime: 30_000,
  });
}

function describeQual(q: ManualQualEntry): string {
  if ('kind' in q && q.kind === 'custom_question') {
    return `Custom question: ${q.question}`;
  }
  const attr = q as AttrQualification;
  return attr.attributeKey;
}

function describeRule(q: ManualQualEntry): string {
  if ('kind' in q && q.kind === 'custom_question') {
    return `${q.options.length} option${q.options.length === 1 ? '' : 's'} · ${q.required.length === 0 ? 'data-collection only' : `requires: ${q.required.join(', ')}`}`;
  }
  const attr = q as AttrQualification;
  switch (attr.match.kind) {
    case 'choice':
      return `is one of ${attr.match.values.join(', ')}`;
    case 'multi_select':
      return `${attr.match.require_any !== false ? 'any of' : 'all of'}: ${attr.match.values.join(', ')}`;
    case 'range':
      return `range: ${attr.match.min ?? '∞'}–${attr.match.max ?? '∞'}`;
    case 'age_range':
      return `Age ${attr.match.minAge}–${attr.match.maxAge}`;
  }
}

function RedirectUrlRow({ outcome, url }: { outcome: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-3 bg-slate-900 rounded-lg px-3 py-2 border border-slate-700">
      <span className="w-20 text-xs uppercase tracking-wide text-slate-400">{outcome}</span>
      <code className="flex-1 text-xs text-slate-200 font-mono break-all">{url}</code>
      <button
        onClick={() => {
          void navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          });
        }}
        className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-slate-700"
      >
        <Copy className="w-3 h-3" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}
